import React, { useState, useEffect } from "react";
import {
  Calendar,
  Star,
  User,
  Video,
  Gift,
  ChevronLeft,
} from "lucide-react";
import sanityClient from "../../client";
import { cn } from "../../lib/utils";

const IntroPage = () => {
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState("15 Min");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const [isLoadingImage, setIsLoadingImage] = useState(true);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "profile-image"] {
          Title,
          Image {
            asset-> {
              url,
              metadata {
                dimensions
              }
            }
          }
        }`
      )
      .then((data) => {
        if (data.length > 0 && data[0].Image) {
          setProfileImage(data[0].Image.asset.url);
        }
        setIsLoadingImage(false);
      })
      .catch((err) => {
        console.error("Error fetching profile image:", err);
        setIsLoadingImage(false);
      });
  }, []);

  const timeSlots = [
    { time: "9:00 AM", available: true },
    { time: "9:20 AM", available: false },
    { time: "9:40 AM", available: true },
    { time: "10:00 AM", available: true },
    { time: "10:20 AM", available: true },
    { time: "10:40 AM", available: false },
  ];

  const fridaySlots = [
    { time: "11:00 AM", available: true },
    { time: "11:20 AM", available: true },
    { time: "2:00 PM", available: true },
    { time: "3:00 PM", available: false },
  ];

  const reviews = [
    {
      name: "Ananda",
      date: "Nov 2022",
      rating: 5,
      text: "Krushil was great. The feedback he gave was spot on, product specific and actionable. Highly recommend.",
    },
    {
      name: "Charlie",
      date: "May 2022",
      rating: 5,
      text: "We're early stage, so I had to fight a tight ad budget for a call with Krushil, but it was worth every cent. Krushil gave incredible and specific feedback on our onboarding and growth. I was writing insights, allowing us to spend our time and resources working on what will move the needle in the most efficient and calculated way. Cheers, Krushil",
    },
    {
      name: "Nathan",
      date: "Jun 2023",
      rating: 5,
      text: "great",
    },
    {
      name: "Josh",
      date: "Apr 2023",
      rating: 5,
      text: "Amazing session on full-stack development",
    },
    {
      name: "Sophie",
      date: "Mar 2023",
      rating: 5,
      text: "Extremely knowledgeable and practical guidance",
    },
    {
      name: "Julia",
      date: "Feb 2023",
      rating: 5,
      text: "Very helpful and insightful. Great advice on React best practices.",
    },
    {
      name: "Jeremy",
      date: "Jan 2023",
      rating: 5,
      text: "Krushil was very experienced and guided me through complex architecture decisions",
    },
  ];

  const faqs = [
    "How does a 1:1 video consultation work?",
    "How long are the sessions?",
    "How much can we accomplish in a virtual session?",
    "Can I book an expert more than once?",
    "Why are these experts doing this?",
    "What timezones are the video consultations based in?",
    "Do you support international customers?",
    "What if an expert is sold out?",
    "When do experts unlock more times?",
    "Can I reschedule or cancel my session?",
    "Do you have a guarantee in case I was dissatisfied?",
  ];

  const getPriceForDuration = (duration) => {
    switch (duration) {
      case "15 Min":
        return 100;
      case "30 Min":
        return 150;
      case "45 Min":
        return 200;
      default:
        return 150;
    }
  };

  return (
    <div className="min-h-screen bg-background text-darkgray font-sans">
      {/* Header */}
      <header className="bg-white border-b border-border px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ChevronLeft className="w-4 h-4 text-gray" />
            <span className="text-gray text-sm">Expert Marketplace</span>
            <span className="text-lightgray text-sm">/ Krushil Amrutiya</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray">Powered by</span>
            <span className="font-bold text-darkgray">intro</span>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Sidebar - Profile */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-border overflow-hidden transition-all duration-200 hover:shadow-md">
              {/* Profile Image and Badge */}
              <div className="relative">
                {isLoadingImage ? (
                  <div className="w-full h-64 bg-accent flex items-center justify-center">
                    <span className="text-gray">Loading...</span>
                  </div>
                ) : (
                  <img
                    src={
                      profileImage ||
                      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    }
                    alt="Krushil Amrutiya Profile"
                    className="w-full h-64 object-cover"
                  />
                )}
                <div className="absolute top-4 left-4 bg-orange text-white px-3 py-1 rounded text-sm font-medium">
                  Top Expert
                </div>
              </div>

              {/* Profile Info */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h1 className="text-xl font-bold text-darkgray">
                    Krushil Amrutiya
                  </h1>
                </div>
                <p className="text-sm text-gray mb-4 leading-relaxed">
                  Full-Stack Developer specializing in minimalist, functional,
                  and accessible digital experiences. React & JavaScript expert.
                  Former PM at various startups. Founded multiple web
                  applications. Expert in modern development practices.
                </p>
                <div className="flex items-center mb-4">
                  <div className="flex text-orange">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm font-medium text-darkgray">
                    5.0 (124)
                  </span>
                </div>
              </div>
            </div>

            {/* About Me */}
            <div className="bg-white rounded-lg shadow-sm border border-border mt-6 p-6 transition-all duration-200 hover:shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-darkgray">About me</h3>
              </div>
              <p className="text-sm text-gray mb-4 leading-relaxed">
                Krushil is a passionate full-stack developer who specializes in
                creating minimalist, accessible web applications. With expertise
                in React, JavaScript, and modern web technologies.
              </p>
              <button className="w-full bg-accent text-gray py-2 px-4 rounded-md text-sm font-medium hover:bg-muted transition-colors">
                View more
              </button>
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-lg shadow-sm border border-border mt-6 p-6 transition-all duration-200 hover:shadow-md">
              <div className="flex items-center mb-4">
                <div className="flex text-orange">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <span className="ml-2 text-sm font-medium text-darkgray">
                  5.0 (124)
                </span>
              </div>
              <div className="space-y-4">
                {reviews.map((review, index) => (
                  <div
                    key={index}
                    className="border-b border-border pb-4 last:border-b-0"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm text-darkgray">
                        {review.name}
                      </span>
                      <span className="text-gray text-xs">{review.date}</span>
                    </div>
                    <p className="text-sm text-gray leading-relaxed">
                      {review.text}
                    </p>
                  </div>
                ))}
              </div>
              <button className="w-full bg-accent text-gray py-2 px-4 rounded-md text-sm font-medium mt-4 hover:bg-muted transition-colors">
                More reviews
              </button>
            </div>
          </div>

          {/* Right Column - Booking */}
          <div className="lg:col-span-2">
            {/* Booking Card */}
            <div className="bg-white rounded-lg shadow-sm border border-border p-6 mb-6 transition-all duration-200 hover:shadow-md">
              <div className="flex items-center justify-between mb-6">
                <div className="bg-darkgray text-white px-4 py-2 rounded-md">
                  <span className="text-sm font-medium">Book a video call</span>
                </div>
                <div className="text-sm text-gray">
                  Powered by{" "}
                  <span className="font-bold text-darkgray">intro</span>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-darkgray mb-2">
                Book a video call
              </h2>

              {/* Duration Pills */}
              <div className="flex flex-wrap gap-3 mb-6">
                {["15 Min", "30 Min", "45 Min"].map((duration) => (
                  <button
                    key={duration}
                    onClick={() => {
                      setSelectedDuration(duration);
                      setSelectedTime(null);
                    }}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200 cursor-pointer border",
                      duration === selectedDuration
                        ? "bg-orange text-white border-orange"
                        : "bg-accent text-gray hover:bg-orange/10 hover:text-orange border-border"
                    )}
                  >
                    {duration}
                  </button>
                ))}
              </div>

              <div className="bg-gradient-to-r from-orange/10 to-orange/20 border border-orange/20 rounded-lg p-3 mb-4">
                <p className="text-orange text-sm font-medium flex items-center">
                  <span className="text-lg mr-2">🎁</span>
                  Tap to send this as a gift
                </p>
              </div>

              {/* Time Slots */}
              <div className="mb-8">
                <h3 className="font-semibold text-darkgray mb-4">Today</h3>
                <div className="grid grid-cols-3 gap-3">
                  {timeSlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() =>
                        slot.available && setSelectedTime(slot.time)
                      }
                      className={cn(
                        "py-3 px-4 rounded-md text-sm font-medium transition-all duration-200 relative",
                        slot.available
                          ? selectedTime === slot.time
                            ? "bg-orange text-white shadow-md transform hover:scale-105"
                            : "bg-gradient-to-r from-white to-accent text-gray hover:from-orange/10 hover:to-orange/20 hover:text-orange border border-border hover:border-orange/30 shadow-sm hover:shadow-md transform hover:scale-105"
                          : "bg-gradient-to-r from-orange/5 to-orange/10 text-orange/50 cursor-not-allowed border-2 border-orange/20"
                      )}
                      disabled={!slot.available}
                    >
                      <span
                        className={
                          !slot.available
                            ? "line-through decoration-2 decoration-orange/30"
                            : ""
                        }
                      >
                        {slot.time}
                      </span>
                      {!slot.available && (
                        <div className="absolute bottom-1 right-1">
                          <span className="text-xs text-orange font-bold bg-orange/10 px-1 py-0.5 rounded">
                            Booked
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Friday's Time Slots */}
              <div className="mb-8">
                <h3 className="font-semibold text-darkgray mb-4">Friday 7/4</h3>
                <div className="grid grid-cols-3 gap-3">
                  {fridaySlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() =>
                        slot.available && setSelectedTime(slot.time)
                      }
                      className={cn(
                        "py-3 px-4 rounded-md text-sm font-medium transition-all duration-200 relative",
                        slot.available
                          ? selectedTime === slot.time
                            ? "bg-orange text-white shadow-md transform hover:scale-105"
                            : "bg-gradient-to-r from-white to-accent text-gray hover:from-orange/10 hover:to-orange/20 hover:text-orange border border-border hover:border-orange/30 shadow-sm hover:shadow-md transform hover:scale-105"
                          : "bg-gradient-to-r from-orange/5 to-orange/10 text-orange/50 cursor-not-allowed border-2 border-orange/20"
                      )}
                      disabled={!slot.available}
                    >
                      <span
                        className={
                          !slot.available
                            ? "line-through decoration-2 decoration-orange/30"
                            : ""
                        }
                      >
                        {slot.time}
                      </span>
                      {!slot.available && (
                        <div className="absolute bottom-1 right-1">
                          <span className="text-xs text-orange font-bold bg-orange/10 px-1 py-0.5 rounded">
                            Booked
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="text-center mb-6">
                <p className="text-gray text-sm">
                  Looking for a new call time?
                </p>
                <button className="text-orange text-sm font-medium hover:underline">
                  Tap here to request a listing
                </button>
              </div>

              {/* Pricing Section */}
              <div className="bg-accent rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="font-bold text-xl text-darkgray">
                      ${getPriceForDuration(selectedDuration)}
                    </span>
                    <span className="text-sm text-gray">
                      for {selectedDuration.toLowerCase()} session
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex text-orange">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <span className="text-lg font-medium text-darkgray">
                      5.0
                    </span>
                    <span className="text-gray">(124)</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button className="flex items-center justify-center w-16 h-12 border-2 border-border rounded-md hover:border-orange transition-colors">
                  <Gift className="w-5 h-5 text-gray" />
                </button>
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="flex-1 bg-orange text-white py-3 px-6 rounded-md font-medium text-lg hover:bg-orange/90 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>

            {/* What to expect */}
            <div className="bg-white rounded-lg shadow-sm border border-border p-6 mb-6 transition-all duration-200 hover:shadow-md">
              <h3 className="text-lg font-semibold text-darkgray mb-4">
                What to expect
              </h3>
              <div className="space-y-3">
                {[
                  "15 minute session",
                  "ask three or more questions",
                  "discuss React, JavaScript, and web development",
                  "advice on code architecture",
                  "how to improve your development skills",
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 bg-gray rounded-full mt-2"></div>
                    <span className="text-sm text-gray">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* How it works */}
            <div className="bg-white rounded-lg shadow-sm border border-border p-6 mb-6 transition-all duration-200 hover:shadow-md">
              <h3 className="text-lg font-semibold text-darkgray mb-6">
                How it works
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: <User className="w-8 h-8 text-gray" />,
                    title: "Find an expert",
                    description:
                      "Discover and choose from our list of startup and experienced, in-demand experts",
                  },
                  {
                    icon: <Calendar className="w-8 h-8 text-gray" />,
                    title: "Book or subscribe",
                    description:
                      "Book a one-time video call appointment and get advice for your expert anytime",
                  },
                  {
                    icon: <Video className="w-8 h-8 text-gray" />,
                    title: "Virtual consultation",
                    description:
                      "Join the video call or chat session and get immediate expert advice",
                  },
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                      {item.icon}
                    </div>
                    <h4 className="font-medium text-darkgray mb-2">
                      {item.title}
                    </h4>
                    <p className="text-sm text-gray">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Common questions */}
            <div className="bg-white rounded-lg shadow-sm border border-border p-6 transition-all duration-200 hover:shadow-md">
              <h3 className="text-lg font-semibold text-darkgray mb-6">
                Common questions
              </h3>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 border-b border-border last:border-b-0"
                  >
                    <span className="text-gray text-sm">{faq}</span>
                    <span className="text-gray text-xl">+</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-darkgray text-white py-12 mt-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-bold text-lg mb-4">intro</h4>
              <p className="text-stone-400 text-sm mb-4">
                Book the most in-demand experts & get advice over a video call
              </p>
            </div>
            <div>
              <h5 className="font-medium mb-4">Company</h5>
              <ul className="space-y-2 text-sm text-stone-400">
                <li>Contact</li>
                <li>Careers</li>
                <li>FAQ</li>
                <li>Gift a session</li>
                <li>Suggest a new topic or expert</li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium mb-4">Support</h5>
              <ul className="space-y-2 text-sm text-stone-400">
                <li>Contact</li>
                <li>Give us feedback & earn</li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium mb-4">Follow us</h5>
              <div className="flex space-x-4">
                <div className="w-8 h-8 bg-gray/20 rounded-md hover:bg-gray/30 transition-colors cursor-pointer"></div>
                <div className="w-8 h-8 bg-gray/20 rounded-md hover:bg-gray/30 transition-colors cursor-pointer"></div>
              </div>
            </div>
          </div>
          <div className="border-t border-stone-400/20 mt-8 pt-8 text-center text-stone-400 text-sm">
            © intro 2024. All rights reserved. • Privacy • Terms
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-darkgray/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="bg-darkgray text-white px-6 py-4 flex items-center justify-between">
              <span className="text-lg font-medium">Book a video call</span>
              <button
                onClick={() => setShowBookingModal(false)}
                className="text-white hover:text-gray transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <h3 className="text-xl font-bold text-darkgray mb-4">
                Confirm your booking
              </h3>

              {selectedTime && (
                <div className="bg-accent rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray mb-2">Selected time:</p>
                  <p className="font-semibold text-darkgray">
                    {selectedTime} - {selectedDuration.toLowerCase()} session
                  </p>
                </div>
              )}

              <div className="bg-accent rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg text-darkgray">
                    ${getPriceForDuration(selectedDuration)}
                  </span>
                  <div className="flex items-center">
                    <div className="flex text-orange">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <span className="ml-2 text-sm">5.0 (124)</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowBookingModal(false);
                  alert(
                    "Booking confirmed! You will receive a confirmation email with the meeting link shortly."
                  );
                }}
                className="w-full bg-orange text-white py-3 px-6 rounded-md font-medium text-lg hover:bg-orange/90 transition-colors"
              >
                Confirm Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntroPage;
